// 월 이용료 — 청구서 조회·결제 승인 콜러블과 토스 결제창 호출
import { httpsCallable } from "firebase/functions";
import { loadTossPayments } from "@tosspayments/tosspayments-sdk";
import { functions } from "./firebase";

export interface InvoiceView {
  yearMonth: string;
  studentCount: number;
  unitPrice: number;
  supplyAmount: number;
  vat: number;
  amount: number;
  orderId: string;
  orderName: string;
  status: "none" | "free" | "ready" | "confirming" | "paid";
  usage: { ata?: number; sms?: number; lms?: number } | null;
  studentSupply: number | null; // 2026-09-28 이후 청구서에만 있음(옛 청구서는 null)
  noticeSupply: number | null;
  noticePrices: { sms: number; lms: number; ata: number } | null;
  transferRequested: boolean;
  transferLocked?: boolean; // 계좌 안내가 나가 앱에서 요청을 취소할 수 없음(전화로만). 예전 서버 응답에는 없다
  confirmingAt: number | null; // '확인 중'에 들어간 시각(ms). 30분 넘으면 화면이 전화 안내를 띄운다
  method: string | null;
  receiptUrl: string | null;
  approvedAt: string | null;
  dueDate?: string | null; // 결제 기한 YYYY-MM-DD(청구일부터 14일) — 결제 전·확인 중일 때만. 예전 서버 응답에는 없다
}

/** "2026-11-15" → "11월 15일" */
export function koDueDate(ymd: string): string {
  return `${Number(ymd.slice(5, 7))}월 ${Number(ymd.slice(8, 10))}일`;
}

/** 「금액이 이상해요」로 낸 이의 — 처리 결과는 콕소프트가 적는다 */
export interface DisputeView {
  yearMonth: string;
  reason: string;
  status: "open" | "resolved";
  resolution: string | null;
  createdAt: number | null;
  resolvedAt: number | null;
}

export interface BillingResult {
  exempt: boolean;
  invoice: InvoiceView | null;
  history: InvoiceView[];
  disputes?: DisputeView[]; // 예전 서버 응답에는 없다
  current: { yearMonth: string; studentCount: number };
}

const _dispute = httpsCallable<{ yearMonth: string; reason: string }, { ok: boolean; already: boolean; notified: boolean }>(functions, "reportBillingDispute");

/** 금액 이의 — 기록하고 콕소프트 대표 번호로 알린다. 금액은 자동으로 바뀌지 않는다 */
export async function reportBillingDispute(yearMonth: string, reason: string) {
  return (await _dispute({ yearMonth, reason })).data;
}

const _get = httpsCallable<{ yearMonth?: string }, BillingResult>(functions, "getBillingInvoice");
const _confirm = httpsCallable<
  { paymentKey: string; orderId: string; amount: number },
  { ok: boolean; alreadyPaid?: boolean; pending?: boolean }
>(functions, "confirmBillingPayment");

export async function getBillingInvoice(yearMonth?: string): Promise<BillingResult> {
  return (await _get(yearMonth ? { yearMonth } : {})).data;
}

const _transfer = httpsCallable<{ yearMonth: string; cancel?: boolean }, { ok: boolean; notified: boolean }>(functions, "requestTransferPayment");

/** 계좌이체·세금계산서로 내겠다고 요청 — 대표 번호로 문자가 가고 계좌는 따로 안내한다 */
export async function requestTransferPayment(yearMonth: string, cancel = false) {
  return (await _transfer(cancel ? { yearMonth, cancel: true } : { yearMonth })).data;
}

const _cancel = httpsCallable<{ reason?: string; cancel?: boolean }, { ok: boolean; notified: boolean; unpaid: { yearMonth: string; amount: number; status: string }[] }>(functions, "requestCancellation");

/** 해지 신청(또는 신청 거두기) — 대표 번호로 문자가 가고, 확정·자료 파기는 사람이 한다 */
export async function requestCancellation(reason?: string, cancel = false) {
  return (await _cancel(cancel ? { cancel: true } : { reason })).data;
}

export async function confirmBillingPayment(input: { paymentKey: string; orderId: string; amount: number }) {
  return (await _confirm(input)).data;
}

// 결제창형 연동 키(gck). 서버 TOSS_SECRET_KEY(gsk)와 같은 가맹점 한 쌍이어야 한다.
// 비어 있으면 테스트 키로 넘어가지 않고 멈춘다 — 키 짝이 어긋나면 결제가 조용히 실패한다.
const CLIENT_KEY = (import.meta.env.VITE_TOSS_CLIENT_KEY ?? "").trim();

let openWindow: { destroy: () => void } | null = null;

/**
 * 토스 결제위젯을 띄운다. 결과는 successUrl/failUrl로 이 화면에 돌아온다.
 * 콕소프트 키는 "결제창형(gck)" 키라 위젯 API만 된다 — 2026-09-29 실측: payment() API는 "API 개별 연동 키의 클라이언트 키로 연동해주세요"로 거부.
 * 카드만 보이게 하는 것은 토스 관리자(결제위젯 → 결제수단 설정)에서 다른 수단을 끄는 것으로 한다(대표 결정 2026-09-28 "카드만").
 */
export async function openPaymentWindow(inv: InvoiceView, orgId: string, onError: (message: string) => void) {
  if (!CLIENT_KEY) throw new Error("결제 설정이 준비되지 않았습니다. 결제는 진행되지 않았습니다.");
  openWindow?.destroy();
  openWindow = null;

  const toss = await loadTossPayments(CLIENT_KEY);
  const widgets = toss.widgets({ customerKey: `org_${orgId}` });
  await widgets.setAmount({ currency: "KRW", value: inv.amount });
  const win = await widgets.renderPaymentWindow({
    variantKey: { paymentMethod: "DEFAULT", agreement: "AGREEMENT" },
  });
  openWindow = win;
  win.on("paymentRequest", async () => {
    try {
      await widgets.requestPayment({
        orderId: inv.orderId,
        orderName: inv.orderName,
        successUrl: `${window.location.origin}/admin/billing?result=success`,
        failUrl: `${window.location.origin}/admin/billing?result=fail`,
      });
    } catch (e) {
      // 창을 그냥 닫은 것은 오류가 아니다 — 돈이 안 나갔다는 말만 남긴다
      const code = (e as { code?: string })?.code || "";
      if (code === "USER_CANCEL" || code === "PAY_PROCESS_CANCELED") return;
      // 결제창 안에서 난 오류는 위로 전달되지 않으므로 화면에 직접 알린다
      onError(`결제창 오류 — ${(e as { message?: string })?.message || "알 수 없는 오류"}. 결제는 진행되지 않았습니다.`);
    }
  });
}
